import defaultLogo from '../../assets/img/ic-img.svg';
import icLink from '../../assets/img/ic-link-square.svg';
import icMail from '../../assets/img/ic-mail-send.svg';
import icPhone from '../../assets/img/ic-smart-phone.svg';
import icHeartEmpty from '../../assets/img/ic-heart-empty.svg';
import icHeartFull from '../../assets/img/ic-heart-full.svg';
import formatDiscoveryUpdateDate from './formatDiscoveryUpdateDate';

const formatLocation = (company, hideCity) => {
    const parts = [company.country, !hideCity && (company.cityRegion || company.city)].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'n/a';
};

const getRecruitingPortalUrl = (url) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `https://${url}`;
};

const renderMobileJobCount = (studentValue, companyValue = 0) => {
    const student = Number(studentValue) || 0;
    const company = Number(companyValue) || 0;

    if (!student && !company) {
        return <span className="mobile-company-card__stat-value is-muted">n/a</span>;
    }

    if (company) {
        return (
            <span className="mobile-company-card__stat-value">
                {company} <span className="mobile-company-card__stat-secondary">({student})</span>
            </span>
        );
    }

    return <span className="mobile-company-card__stat-value">{student}</span>;
};

const MobileCompanyCard = ({
    company,
    isFavorite = false,
    hideCity = false,
    onFavoriteToggle,
    onOpenCompany,
}) => {
    const rating = parseFloat(company.bestPlacetoWorkRating || 0);
    const hasGptwRating = rating > 89;
    const recruitingPortalUrl = getRecruitingPortalUrl(company.hrRecrutingUrl);
    const hasHrContact = Boolean(company.hrEmail || company.hrPhone);
    const hasContactRow = Boolean(hasHrContact || recruitingPortalUrl);
    const jobStats = [
        { label: 'Student', value: company.openJobsStudent },
        { label: 'Intern', value: company.openJobsIntern },
        { label: 'Entry', value: company.openJobsStarting },
        { label: '1-3 yr', value: company.openJobs13 },
        { label: '3+ yr', value: company.openJobs3 },
        { label: 'Total', value: company.openJobsTotal, isTotal: true },
    ];

    const handleCardClick = () => {
        if (onOpenCompany) {
            onOpenCompany(company);
        }
    };

    const handleCardKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleCardClick();
        }
    };

    const handleFavoriteClick = (event) => {
        event.stopPropagation();
        if (onFavoriteToggle) {
            onFavoriteToggle(company._id);
        }
    };

    const handleCopyContact = (event, value) => {
        event.stopPropagation();
        if (value && navigator.clipboard) {
            navigator.clipboard.writeText(String(value));
        }
    };

    return (
        <article
            className="mobile-company-card"
            role="button"
            tabIndex={0}
            onClick={handleCardClick}
            onKeyDown={handleCardKeyDown}
        >
            <div className="mobile-company-card__body">
                <div className="mobile-company-card__top">
                    <div className="mobile-company-card__identity">
                        <div className="mobile-company-card__logo">
                            <img
                                src={company.logo ? company.logo : defaultLogo}
                                alt={company.title || 'Company logo'}
                            />
                        </div>

                        <div className="mobile-company-card__meta">
                            <h3 className="mobile-company-card__title mb-0">
                                {company.title || 'n/a'}
                            </h3>
                            <p className="mobile-company-card__location">
                                {formatLocation(company, hideCity)}
                            </p>
                        </div>
                    </div>

                    <div className="mobile-company-card__actions">
                        {hasGptwRating ? (
                            <div
                                className="mobile-company-card__gptw"
                                aria-label={`Great Place to Work rating ${rating}%`}
                            >
                                <span className="mobile-company-card__gptw-label">GPTW</span>
                                <span className="mobile-company-card__gptw-score">{rating}%</span>
                            </div>
                        ) : null}

                        <button
                            className={`mobile-company-card__favorite${isFavorite ? ' is-active' : ''}`}
                            type="button"
                            aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                            aria-pressed={isFavorite}
                            onClick={handleFavoriteClick}
                        >
                            <img
                                src={isFavorite ? icHeartFull : icHeartEmpty}
                                alt=""
                                aria-hidden="true"
                            />
                        </button>
                    </div>
                </div>

                {hasContactRow ? (
                    <div className="mobile-company-card__contact-row">
                        {hasHrContact ? (
                            <div className="mobile-company-card__hr-actions">
                                <span className="mobile-company-card__hr-label">HR:</span>

                                {company.hrEmail ? (
                                    <button
                                        className="mobile-company-card__contact-action"
                                        type="button"
                                        aria-label="Copy HR email"
                                        onClick={(event) => handleCopyContact(event, company.hrEmail)}
                                    >
                                        <img src={icMail} alt="" aria-hidden="true" />
                                    </button>
                                ) : null}

                                {company.hrPhone ? (
                                    <button
                                        className="mobile-company-card__contact-action"
                                        type="button"
                                        aria-label="Copy HR phone"
                                        onClick={(event) => handleCopyContact(event, company.hrPhone)}
                                    >
                                        <img src={icPhone} alt="" aria-hidden="true" />
                                    </button>
                                ) : null}
                            </div>
                        ) : null}

                        {recruitingPortalUrl ? (
                            <a
                                className="mobile-company-card__recruiting-link"
                                href={recruitingPortalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(event) => event.stopPropagation()}
                            >
                                <span>Recruiting portal</span>
                                <img src={icLink} alt="" aria-hidden="true" />
                            </a>
                        ) : null}
                    </div>
                ) : null}

                <p className="mobile-company-card__updated-at mobile-company-card__updated-at--standalone">
                    Updated at: {formatDiscoveryUpdateDate(company)}
                </p>
            </div>

            <div className="mobile-company-card__stats-strip" aria-label="Job statistics">
                {jobStats.map((stat) => (
                    <div
                        className={`mobile-company-card__stat-cell${stat.isTotal ? ' is-total' : ''}`}
                        key={stat.label}
                    >
                        <span className="mobile-company-card__stat-label">{stat.label}</span>
                        {renderMobileJobCount(stat.value)}
                    </div>
                ))}
            </div>
        </article>
    );
};

export default MobileCompanyCard;
